/**
 * The feed's paging rules (spec 9.3: "one card per screen, snapped"), kept free of the DOM so
 * they can be tested on their own. The feed no longer uses the browser's scroll snapping: on a
 * phone it eased the last stretch into place over most of a second, let a short flick spring
 * back, and scrolled the page, so Brave's toolbars slid in and out and resized every screen.
 * Like a reels feed, the card now follows the finger and a release turns exactly one page.
 */

/** How long a page turn takes. Short enough to feel instant, long enough to see the motion. */
export const PAGE_MS = 260;

/** A drag this far, as a share of the screen height, turns the page even without a flick. */
export const DISTANCE_SHARE = 0.12;

/** A release faster than this (px per ms) turns the page however short the drag was... */
export const FLICK_VELOCITY = 0.3;

/** ...as long as it moved at least this far, so a tap that wobbles is not a flick. */
export const MIN_FLICK_PX = 12;

/** Movement below this is still a tap; past it the gesture picks an axis and keeps it. */
export const AXIS_SLOP_PX = 6;

/** At either end of the list the card still moves, but only this share of the finger's way. */
export const EDGE_RESISTANCE = 0.3;

/**
 * How far the current card is drawn for a drag of `dy` px (negative is up, towards the next
 * card). Past the first or the last screen it resists, so the end of the list is felt rather
 * than hit — the forward cap (only `furthest + 1` screens exist) needs nothing else.
 */
export function dragOffset(dy: number, index: number, count: number): number {
  const pastStart = index === 0 && dy > 0;
  const pastEnd = index >= count - 1 && dy < 0;
  return pastStart || pastEnd ? dy * EDGE_RESISTANCE : dy;
}

export interface Release {
  index: number;
  count: number;
  /** The total drag in px when the finger lifted; negative is up. */
  dy: number;
  /** px per ms over the last moments of the drag; negative is up. */
  velocity: number;
  /** The screen height in px. */
  height: number;
}

/** The screen a released drag lands on: one page on, one page back, or where it started. */
export function releaseTarget({ index, count, dy, velocity, height }: Release): number {
  const far = Math.abs(dy) >= height * DISTANCE_SHARE;
  const flick = Math.abs(velocity) >= FLICK_VELOCITY && Math.abs(dy) >= MIN_FLICK_PX;
  if (!far && !flick) return index;
  // A flick against the drag (pulled down, then flung up) goes where the flick points.
  const direction = flick ? Math.sign(velocity) : Math.sign(dy);
  const target = direction < 0 ? index + 1 : index - 1;
  return Math.min(Math.max(target, 0), count - 1);
}

/** Velocity from the last ~100 ms of samples, so a drag that paused before lifting is slow. */
export function releaseVelocity(samples: { y: number; t: number }[]): number {
  const last = samples[samples.length - 1];
  if (!last) return 0;
  const first = samples.find((sample) => last.t - sample.t <= 100) ?? last;
  const elapsed = last.t - first.t;
  return elapsed > 0 ? (last.y - first.y) / elapsed : 0;
}
